import * as React from 'react';
import { searchTagNames } from '../pages/PlaceListPage.logic';
import { PlaceFilterOperatorLabels, PlaceFilterPopover } from './PlaceFilterPopover';
import './PlaceFilterDropdown.css';

interface PlaceFilterDropdownProps<T extends { name: string }> {
  label: string;
  items: T[];
  isSelected: (item: T) => boolean;
  onToggle: (item: T) => void;
  // 一括チェック。渡した項目をまとめて選ぶ（selected=false なら外す）
  onSetSelected: (items: T[], selected: boolean) => void;
  getKey: (item: T) => string;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  negated?: boolean;
  onNegatedChange?: (negated: boolean) => void;
  operatorLabels?: PlaceFilterOperatorLabels;
  onRemove?: () => void;
}

// 物件一覧の絞り込みボタン（タグ・責任者・お客様）。押すと検索欄つきのチェックリストが開き、項目が増えても探して選べる
export function PlaceFilterDropdown<T extends { name: string }>(props: PlaceFilterDropdownProps<T>) {
  const selectedItems = props.items.filter(item => props.isSelected(item));
  return (
    <PlaceFilterPopover
      label={props.label}
      activeCount={0}
      active={selectedItems.length > 0}
      summary={selectedItems.map(item => item.name).join('、')}
      disabled={props.items.length === 0}
      defaultOpen={props.defaultOpen}
      onOpenChange={props.onOpenChange}
      negated={props.negated}
      onNegatedChange={props.onNegatedChange}
      operatorLabels={props.operatorLabels}
      onRemove={props.onRemove}
    >
      {() => <PlaceFilterChecklist {...props} />}
    </PlaceFilterPopover>
  );
}

// メニューを開くたびに作り直されるので、検索欄は開くたびに空になる
function PlaceFilterChecklist<T extends { name: string }>(props: PlaceFilterDropdownProps<T>) {
  const [query, setQuery] = React.useState<string>('');
  const foundItems = searchTagNames(props.items, query);
  // 一括チェックは、検索で絞り込んでいるときは見えている項目だけが対象
  const foundSelectedCount = foundItems.filter(item => props.isSelected(item)).length;
  const allFoundSelected = foundItems.length > 0 && foundSelectedCount === foundItems.length;
  return (
    <>
      <input
        className="PlaceFilterDropdown-search"
        type="text"
        placeholder={`${props.label}を検索`}
        value={query}
        autoFocus
        onChange={event => setQuery(event.target.value)}
      />
      <div className="PlaceFilterDropdown-list">
        {foundItems.length === 0 && <div className="PlaceFilterDropdown-empty">見つかりません</div>}
        {foundItems.length > 0 && (
          <label className="PlaceFilterDropdown-item PlaceFilterDropdown-select_all">
            <input
              type="checkbox"
              checked={allFoundSelected}
              ref={input => {
                if (input) {
                  input.indeterminate = foundSelectedCount > 0 && !allFoundSelected;
                }
              }}
              onChange={() => props.onSetSelected(foundItems, !allFoundSelected)}
            />
            <span>{query.trim() === '' ? 'すべて選択' : '表示中をすべて選択'}</span>
          </label>
        )}
        {foundItems.map(item => (
          <label key={props.getKey(item)} className="PlaceFilterDropdown-item">
            <input type="checkbox" checked={props.isSelected(item)} onChange={() => props.onToggle(item)} />
            <span>{item.name}</span>
          </label>
        ))}
      </div>
    </>
  );
}
